import { useState, forwardRef, useImperativeHandle, useRef } from "react";
import axios from "axios";
import { Send, CheckCircle2, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const serviceOptions = [
  "Général",
  "Révision et vidange",
  "Freinage",
  "Distribution",
  "Roulements & suspensions",
  "Pré-contrôle technique",
  "Échappement",
  "Pneumatique",
  "Batterie",
  "Réparation moteur",
  "Boîte de vitesses",
  "Changement d'embrayage",
  "Vidange boîte automatique",
  "Recherche de panne",
  "Mécanique technique",
  "Reprogrammation Stage 1",
  "Suppression FAP / EGR / AdBlue",
  "E85 / Flex Fuel",
  "Reprogrammation moteur",
];

const initial = {
  nom: "",
  prenom: "",
  email: "",
  telephone: "",
  immatriculation: "",
  vehicule: "",
  service_type: "Général",
  description: "",
};

const QuoteForm = forwardRef(function QuoteForm(props, ref) {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const sectionRef = useRef(null);

  useImperativeHandle(ref, () => ({
    scrollAndPrefill: (service_type) => {
      sectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      if (service_type) setForm((f) => ({ ...f, service_type }));
    },
  }));

  const onChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await axios.post(`${API}/quotes`, form);
      setSuccess(true);
      setForm(initial);
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        "Une erreur est survenue. Réessayez ou appelez-nous au 06 49 58 06 64.";
      setError(typeof detail === "string" ? detail : "Une erreur est survenue.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="devis"
      ref={sectionRef}
      data-testid="quote-section"
      className="py-24 lg:py-32 bg-slate-50"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-5 gap-10 lg:gap-16 items-start">
          <div className="lg:col-span-2">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600 mb-4">
              Demande de devis
            </div>
            <h2 className="font-display font-black text-4xl md:text-5xl tracking-tighter text-slate-900 mb-5">
              Recevez votre devis <br />
              <span className="text-slate-500">sous 48h.</span>
            </h2>
            <p className="text-slate-600 leading-relaxed mb-8">
              Remplissez le formulaire avec les détails de votre véhicule et du
              service souhaité. Notre équipe vous revient sous 48h ouvrées avec
              une estimation claire.
            </p>

            <div className="space-y-3">
              {[
                "Devis gratuit et sans engagement",
                "Garage multimarque certifié",
                "Tarifs transparents et compétitifs",
              ].map((t) => (
                <div key={t} className="flex items-center gap-3 text-slate-700">
                  <CheckCircle2 size={18} className="text-blue-600 shrink-0" />
                  <span className="text-[15px]">{t}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-3">
            <form
              onSubmit={onSubmit}
              data-testid="quote-form"
              className="bg-white border border-slate-200 rounded-lg p-6 md:p-8 shadow-sm"
            >
              {success ? (
                <div
                  data-testid="quote-success"
                  className="py-10 text-center"
                >
                  <div className="h-14 w-14 mx-auto grid place-items-center rounded-full bg-blue-50 text-blue-600 mb-5">
                    <CheckCircle2 size={28} />
                  </div>
                  <h3 className="font-display font-black text-2xl text-slate-900 mb-2">
                    Merci pour votre demande !
                  </h3>
                  <p className="text-slate-600 max-w-md mx-auto">
                    Nous vous recontactons sous 48h ouvrées pour vous
                    transmettre votre devis.
                  </p>
                  <button
                    type="button"
                    data-testid="quote-reset"
                    onClick={() => setSuccess(false)}
                    className="mt-6 text-sm font-semibold text-blue-600 hover:text-blue-700"
                  >
                    Faire une autre demande
                  </button>
                </div>
              ) : (
                <>
                  <div className="grid md:grid-cols-2 gap-4">
                    <Field label="Nom *" name="nom" value={form.nom} onChange={onChange} required />
                    <Field label="Prénom *" name="prenom" value={form.prenom} onChange={onChange} required />
                    <Field label="Email *" name="email" type="email" value={form.email} onChange={onChange} required />
                    <Field label="Téléphone *" name="telephone" type="tel" value={form.telephone} onChange={onChange} required />
                    <Field label="Immatriculation *" name="immatriculation" value={form.immatriculation} onChange={onChange} required placeholder="AA-123-AA" />
                    <Field label="Marque & modèle *" name="vehicule" value={form.vehicule} onChange={onChange} required placeholder="Ex : Peugeot 308 1.6 HDi" />
                  </div>

                  <div className="mt-4">
                    <label className="block text-sm font-semibold text-slate-800 mb-1.5">
                      Service souhaité
                    </label>
                    <select
                      data-testid="quote-service-type"
                      name="service_type"
                      value={form.service_type}
                      onChange={onChange}
                      className="w-full rounded-md border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                    >
                      {serviceOptions.map((o) => (
                        <option key={o} value={o}>
                          {o}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="mt-4">
                    <label className="block text-sm font-semibold text-slate-800 mb-1.5">
                      Décrivez votre besoin *
                    </label>
                    <textarea
                      data-testid="quote-description"
                      name="description"
                      value={form.description}
                      onChange={onChange}
                      required
                      rows={5}
                      className="w-full rounded-md border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 resize-none"
                      placeholder="Symptômes, kilométrage, contexte…"
                    />
                  </div>

                  {error && (
                    <div
                      data-testid="quote-error"
                      className="mt-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
                    >
                      {error}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="quote-submit"
                    className="lift mt-6 w-full inline-flex items-center justify-center gap-2 rounded-md bg-slate-900 hover:bg-blue-600 disabled:bg-slate-400 px-5 py-3.5 text-sm font-semibold text-white shadow-sm transition-colors"
                  >
                    {loading ? (
                      <>
                        <Loader2 size={16} className="animate-spin" /> Envoi en cours…
                      </>
                    ) : (
                      <>
                        Obtenir mon devis <Send size={16} />
                      </>
                    )}
                  </button>
                  <p className="mt-3 text-xs text-slate-500">
                    Les devis vous sont envoyés sous 48 heures ouvrées.
                  </p>
                </>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
});

function Field({ label, ...props }) {
  return (
    <div>
      <label className="block text-sm font-semibold text-slate-800 mb-1.5">
        {label}
      </label>
      <input
        {...props}
        data-testid={`quote-field-${props.name}`}
        className="w-full rounded-md border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
      />
    </div>
  );
}

export default QuoteForm;
